import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { graphqlApi } from '../../../services/graphql';
import { socialConnectService } from '../../../services/socialConnect';
import { useAuthStore } from '../../../store/authStore';
import type { Influencer } from '../../../types';

import { INTEREST_CATEGORIES, ACTIVITY_TYPES, EQUIPMENT_TYPES, MARITAL_STATUS, CHILDREN_STATUS } from '../../../constants/categories';

const SNS_URL_PREFIXES: Record<string, string> = {
  instagram: 'https://www.instagram.com/',
  naverblog: 'https://blog.naver.com/',
  youtube: 'https://www.youtube.com/',
};

function extractSnsId(key: string, value: string): string {
  const prefix = SNS_URL_PREFIXES[key];
  if (prefix && value.startsWith(prefix)) return value.slice(prefix.length);
  return value;
}

function buildSnsUrl(key: string, id: string): string {
  if (!id) return '';
  const prefix = SNS_URL_PREFIXES[key];
  return prefix ? `${prefix}${id}` : id;
}

const SNS_CHANNELS = [
  {
    key: 'instagram' as const,
    label: '인스타그램',
    icon: (
      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none">
        <rect x="2" y="2" width="20" height="20" rx="5" stroke="url(#igP)" strokeWidth="2"/>
        <circle cx="12" cy="12" r="5" stroke="url(#igP)" strokeWidth="2"/>
        <circle cx="17.5" cy="6.5" r="1.5" fill="url(#igP)"/>
        <defs><linearGradient id="igP" x1="2" y1="22" x2="22" y2="2"><stop stopColor="#FFDC80"/><stop offset=".5" stopColor="#F56040"/><stop offset="1" stopColor="#833AB4"/></linearGradient></defs>
      </svg>
    ),
  },
  {
    key: 'naverblog' as const,
    label: '블로그',
    icon: (
      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
        <path d="M16.273 12.845 7.376 0H0v24h7.727V11.155L16.624 24H24V0h-7.727v12.845Z" fill="#03C75A"/>
      </svg>
    ),
  },
  {
    key: 'youtube' as const,
    label: '유튜브',
    icon: (
      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814ZM9.545 15.568V8.432L15.818 12l-6.273 3.568Z" fill="#FF0000"/>
      </svg>
    ),
  },
];

interface ProfileFormState {
  snsChannels: {
    instagram: { account: string; followerCount: number };
    youtube: { account: string; followerCount: number };
    tiktok: { account: string; followerCount: number };
    twitter: { account: string; followerCount: number };
    naverblog: { account: string; followerCount: number };
  };
  categories: string[];
  mainCategory: string;
  activityType: string;
  equipment: string[];
  maritalStatus: string;
  hasChildren: string;
  portfolio: string[];
  ratePerPost: number;
  engagementRate: number;
  bio: string;
  location: string;
  languages: string[];
}

function mapInfluencerToFormState(influencer: Influencer): ProfileFormState {
  const ch = influencer.socialMediaHandles || {};
  return {
    snsChannels: {
      instagram: { account: extractSnsId('instagram', ch.instagram?.account ?? ''), followerCount: ch.instagram?.followerCount ?? 0 },
      youtube: { account: extractSnsId('youtube', ch.youtube?.account ?? ''), followerCount: ch.youtube?.followerCount ?? 0 },
      tiktok: { account: ch.tiktok?.account ?? '', followerCount: ch.tiktok?.followerCount ?? 0 },
      twitter: { account: ch.twitter?.account ?? '', followerCount: ch.twitter?.followerCount ?? 0 },
      naverblog: { account: extractSnsId('naverblog', ch.naverblog?.account ?? ''), followerCount: ch.naverblog?.followerCount ?? 0 },
    },
    categories: influencer.categories ?? [],
    mainCategory: influencer.mainCategory ?? '',
    activityType: (influencer as any).activityType ?? '',
    equipment: (influencer as any).equipment ?? [],
    maritalStatus: (influencer as any).maritalStatus ?? '',
    hasChildren: (influencer as any).hasChildren ?? '',
    portfolio: influencer.portfolio ?? [],
    ratePerPost: influencer.ratePerPost ?? 0,
    engagementRate: influencer.engagementRate ?? 0,
    bio: influencer.bio ?? '',
    location: influencer.location ?? '',
    languages: influencer.languages ?? [],
  };
}

const SOCIAL_PLATFORMS = ['instagram', 'youtube', 'tiktok', 'twitter', 'naverblog'] as const;
type SocialPlatform = (typeof SOCIAL_PLATFORMS)[number];

const ProfileSubPage: React.FC = () => {
  const { t } = useTranslation('profile');
  const { user } = useAuthStore();

  const [influencer, setInfluencer] = useState<Influencer | null>(null);
  const [formState, setFormState] = useState<ProfileFormState | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingChannel, setSavingChannel] = useState<string | null>(null);
  const [connectingYoutube, setConnectingYoutube] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newPortfolioUrl, setNewPortfolioUrl] = useState('');

  const fetchInfluencer = useCallback(async () => {
    if (!user?.userId) {
      setError('User not authenticated');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const response = await graphqlApi.getInfluencerByUserId(user.userId);
      if (response.success && response.data) {
        setInfluencer(response.data);
        setFormState(mapInfluencerToFormState(response.data));
      } else {
        setError(response.error || t('myProfile.profileSubPage.loadFailed'));
      }
    } catch (err: any) {
      console.error('Error fetching influencer profile:', err);
      setError(err.message || t('myProfile.profileSubPage.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [user?.userId, t]);

  useEffect(() => { fetchInfluencer(); }, [fetchInfluencer]);

  const updateSocialHandle = (platform: SocialPlatform, value: string) => {
    setFormState((prev) => prev ? { ...prev, snsChannels: { ...prev.snsChannels, [platform]: { ...prev.snsChannels[platform], account: value } } } : prev);
  };

  const updateFollowerCount = (platform: SocialPlatform, value: number) => {
    setFormState((prev) => prev ? { ...prev, snsChannels: { ...prev.snsChannels, [platform]: { ...prev.snsChannels[platform], followerCount: value } } } : prev);
  };

  // Connect the user's own YouTube channel via Google OAuth (youtube.readonly).
  // Auto-detects the channel id + subscriber count with no typing, and stores
  // the channel id in the `account` field.
  const handleConnectYoutube = async () => {
    setConnectingYoutube(true);
    try {
      const result = await socialConnectService.connectYoutube();
      const name = result.title || '';
      setFormState((prev) => prev ? {
        ...prev,
        snsChannels: {
          ...prev.snsChannels,
          youtube: {
            account: result.channelId || prev.snsChannels.youtube.account,
            followerCount: typeof result.subscriberCount === 'number'
              ? result.subscriberCount
              : prev.snsChannels.youtube.followerCount,
          },
        },
      } : prev);
      if (result.hiddenSubscriberCount) {
        toast.success(t('myProfile.profileSubPage.youtubeConnectedHidden', '{{name}} 채널을 연결했습니다. 구독자 수가 비공개라 직접 입력해 주세요.', { name }));
      } else {
        toast.success(t('myProfile.profileSubPage.youtubeConnected', '{{name}} 채널을 연결했습니다.', { name }));
      }
    } catch (err: any) {
      const msg = err?.message || '';
      if (msg !== 'Popup closed' && msg !== 'Popup blocked') {
        // Surface the real reason (config error, Google token error, YouTube
        // API error) instead of a generic message, so failures are diagnosable.
        console.error('[youtube-connect] failed:', msg);
        toast.error(
          msg
            ? t('myProfile.profileSubPage.youtubeConnectFailed', '유튜브 연결에 실패했습니다.') + ` (${msg})`
            : t('myProfile.profileSubPage.youtubeConnectFailed', '유튜브 연결에 실패했습니다.'),
        );
      }
    } finally {
      setConnectingYoutube(false);
    }
  };

  const toggleCategory = (category: string) => {
    setFormState((prev) => {
      if (!prev) return prev;
      const exists = prev.categories.includes(category);
      return { ...prev, categories: exists ? prev.categories.filter((c) => c !== category) : [...prev.categories, category] };
    });
  };

  const addPortfolioLink = () => {
    const trimmed = newPortfolioUrl.trim();
    if (!trimmed || !formState) return;
    setFormState({ ...formState, portfolio: [...formState.portfolio, trimmed] });
    setNewPortfolioUrl('');
  };

  const removePortfolioLink = (index: number) => {
    setFormState((prev) => prev ? { ...prev, portfolio: prev.portfolio.filter((_, i) => i !== index) } : prev);
  };

  const handleSaveChannel = async (channelKey: string) => {
    if (!influencer?.influencerId || !formState) return;
    setSavingChannel(channelKey);
    try {
      const ch = formState.snsChannels[channelKey as keyof typeof formState.snsChannels];
      const fullUrl = buildSnsUrl(channelKey, (ch?.account || '').trim());
      const followerCount = ch?.followerCount || 0;
      const updatedHandles: Record<string, { account?: string; followerCount?: number }> = {};
      const existingHandles = influencer.socialMediaHandles || {};
      for (const platform of ['instagram', 'twitter', 'tiktok', 'youtube', 'naverblog']) {
        const existing = (existingHandles as any)[platform];
        if (existing?.account || existing?.followerCount) {
          updatedHandles[platform] = { account: existing.account, followerCount: existing.followerCount };
        }
      }
      updatedHandles[channelKey] = { account: fullUrl, followerCount };
      const res = await graphqlApi.updateInfluencer(influencer.influencerId, {
        socialMediaHandles: updatedHandles,
      });
      if (res.success) {
        setInfluencer(prev => prev ? { ...prev, socialMediaHandles: updatedHandles } : prev);
        toast.success(`${SNS_CHANNELS.find(c => c.key === channelKey)?.label || channelKey} 저장 완료`);
      } else {
        toast.error(res.error || '저장에 실패했습니다');
      }
    } catch (err: any) {
      toast.error(err.message || '저장에 실패했습니다');
    } finally {
      setSavingChannel(null);
    }
  };

  const handleSave = async () => {
    if (!influencer?.influencerId || !formState) return;
    try {
      setSaving(true);
      const response = await graphqlApi.updateInfluencer(influencer.influencerId, {
        socialMediaHandles: Object.fromEntries(
          Object.entries(formState.snsChannels)
            .filter(([, v]) => v.account || v.followerCount)
            .map(([k, v]) => [k, { account: buildSnsUrl(k, (v.account || '').trim()), followerCount: v.followerCount }])
        ),
        categories: formState.categories,
        mainCategory: formState.mainCategory,
        activityType: formState.activityType,
        equipment: formState.equipment,
        maritalStatus: formState.maritalStatus,
        hasChildren: formState.hasChildren,
        portfolio: formState.portfolio,
        ratePerPost: formState.ratePerPost,
        engagementRate: formState.engagementRate,
        bio: formState.bio,
        location: formState.location,
        languages: formState.languages,
      });
      if (response.success) {
        toast.success(t('myProfile.profileSubPage.saveSuccess'));
      } else {
        toast.error(response.error || t('myProfile.profileSubPage.saveFailed'));
      }
    } catch (err: any) {
      console.error('Error saving profile:', err);
      toast.error(t('myProfile.profileSubPage.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D5BFF]" />
        <span className="ml-2 text-[#6B7794]">{t('common.loading', 'Loading...')}</span>
      </div>
    );
  }

  if (error || !formState) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12 text-center">
        <p className="text-[#F25151] mb-4">{error || t('myProfile.profileSubPage.loadFailed')}</p>
        <button onClick={fetchInfluencer} className="px-4 py-2.5 text-sm font-bold text-white bg-[#0F1E3D] rounded-lg hover:bg-[#2D5BFF] transition-colors">
          {t('myProfile.profileSubPage.retry')}
        </button>
      </div>
    );
  }

  // Shared chip styling (electric when selected) per the Bright design system.
  const chipCls = (selected: boolean) =>
    `px-4 py-2 text-sm font-medium rounded-lg border-[1.5px] transition ${
      selected
        ? 'border-[#2D5BFF] bg-[#E6EDFF] text-[#2D5BFF] font-semibold'
        : 'border-[#E4E8F0] text-[#0F1E3D] hover:border-[#0F1E3D]'
    }`;
  const inputCls =
    'w-full px-3.5 py-2.5 text-sm rounded-lg border-[1.5px] border-[#E4E8F0] bg-white text-[#0F1E3D] transition focus:outline-none focus:border-[#2D5BFF] focus:ring-2 focus:ring-[#E6EDFF]';
  const sectionHeader = (title: string) => (
    <div className="flex items-center justify-between gap-2 pb-3.5 mb-4 border-b-2 border-[#0F1E3D]">
      <h2 className="text-[18px] font-bold tracking-tight text-[#0F1E3D]">{title}</h2>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 sm:py-8">
      {/* Page heading */}
      <h1 className="text-[26px] sm:text-[28px] font-bold tracking-tight text-[#0F1E3D]">
        {t('myProfile.profileSubPage.title')}
      </h1>
      <p className="text-[13px] text-[#6B7794] mt-1 mb-6">
        {t('myProfile.profileSubPage.pageSub', '경험 공유 채널과 관심사를 등록하면 더 잘 맞는 캠페인을 추천받을 수 있습니다.')}
      </p>

      <div className="bg-white border border-[#E4E8F0] rounded-xl shadow-[0_1px_2px_rgba(15,30,61,.04),0_4px_12px_rgba(15,30,61,.05)] overflow-hidden">
        {/* SNS 채널정보 */}
        <section className="px-6 sm:px-8 py-7">
          <div className="flex items-center justify-between gap-2 pb-3.5 mb-1 border-b-2 border-[#0F1E3D]">
            <h2 className="text-[18px] font-bold tracking-tight text-[#0F1E3D]">SNS 채널정보</h2>
          </div>
          <p className="text-[13px] text-[#6B7794] mb-4">
            정확하지 않은 팔로워 수의 경우 관리자에 의해 수정될 수 있습니다.
          </p>
          <div className="space-y-3">
            {SNS_CHANNELS.map(({ key, label, icon }) => {
              const ch = formState.snsChannels[key];
              const account = ch?.account || '';
              const followers = ch?.followerCount || 0;
              return (
                <div key={key} className="border border-[#E4E8F0] rounded-xl p-4 sm:p-5 bg-white space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="flex-shrink-0">{icon}</div>
                    <span className="text-[13.5px] font-bold text-[#0F1E3D]">{label}</span>
                    {key === 'youtube' && (
                      <button
                        type="button"
                        onClick={handleConnectYoutube}
                        disabled={connectingYoutube}
                        className="ml-auto flex-shrink-0 px-3 py-1.5 text-xs font-bold text-white bg-[#FF0000] rounded-md hover:bg-[#d90000] disabled:bg-[#E4E8F0] disabled:text-[#A0AAC2] disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                      >
                        {connectingYoutube
                          ? t('myProfile.profileSubPage.youtubeConnecting', '연결 중…')
                          : t('myProfile.profileSubPage.youtubeConnect', 'YouTube 인증')}
                      </button>
                    )}
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                    <div className="flex-1">
                      <label className="block font-mono text-[11px] text-[#6B7794] mb-1">계정 아이디</label>
                      <input
                        type="text"
                        value={account}
                        onChange={(e) => updateSocialHandle(key, e.target.value)}
                        readOnly={key === 'youtube'}
                        placeholder={key === 'youtube' ? '@channel_id' : 'account_id'}
                        className={key === 'youtube'
                          ? `${inputCls} bg-[#F4F6FA] text-[#6B7794] cursor-not-allowed`
                          : inputCls}
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block font-mono text-[11px] text-[#6B7794] mb-1">
                        {key === 'youtube' ? '구독자 수' : '팔로워 수'}
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min={0}
                          value={followers || ''}
                          onChange={(e) => updateFollowerCount(key, parseInt(e.target.value) || 0)}
                          readOnly={key === 'youtube'}
                          placeholder="0"
                          className={key === 'youtube'
                            ? `${inputCls} font-mono bg-[#F4F6FA] text-[#6B7794] cursor-not-allowed`
                            : `${inputCls} font-mono`}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Profile Section: Bio + Areas of Interest */}
        <section className="px-6 sm:px-8 py-7 border-t border-[#EEF1F6]">
          {sectionHeader(t('myProfile.profileSubPage.profileSectionTitle'))}
          <p className="text-[13px] text-[#6B7794] -mt-2 mb-4">{t('myProfile.profileSubPage.profileSectionDesc')}</p>
          <div className="space-y-5">
            <div>
              <label className="block text-[13px] font-bold text-[#0F1E3D] mb-2">{t('myProfile.profileSubPage.bio')}</label>
              <textarea
                value={formState.bio}
                onChange={(e) => setFormState({ ...formState, bio: e.target.value })}
                rows={4}
                className={`${inputCls} resize-y leading-relaxed`}
                placeholder={t('myProfile.profileSubPage.bioPlaceholder')}
              />
            </div>
            <div>
              <label className="block text-[13px] font-bold text-[#0F1E3D] mb-2">{t('myProfile.profileSubPage.categories')}</label>
              <div className="flex flex-wrap gap-2">
                {INTEREST_CATEGORIES.map((cat) => (
                  <button key={cat} type="button" onClick={() => toggleCategory(cat)} className={chipCls(formState.categories.includes(cat))}>
                    {t(`common:categories.${cat}`)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Activity Info */}
        <section className="px-6 sm:px-8 py-7 border-t border-[#EEF1F6]">
          {sectionHeader(t('common:activityTypes.title'))}
          <label className="block text-[13px] font-bold text-[#0F1E3D] mb-2">{t('common:activityTypes.activityType')}</label>
          <div className="flex flex-wrap gap-2">
            {ACTIVITY_TYPES.map((type) => (
              <button key={type} type="button" onClick={() => setFormState({ ...formState, activityType: type })} className={chipCls(formState.activityType === type)}>
                {t(`common:activityTypes.${type}`)}
              </button>
            ))}
          </div>

          <label className="block text-[13px] font-bold text-[#0F1E3D] mt-6 mb-2">{t('common:equipmentTypes.title')}</label>
          <div className="flex flex-wrap gap-2">
            {EQUIPMENT_TYPES.map((eq) => {
              const selected = formState.equipment.includes(eq);
              return (
                <button key={eq} type="button"
                  onClick={() => setFormState((prev) => prev ? { ...prev, equipment: selected ? prev.equipment.filter((e) => e !== eq) : [...prev.equipment, eq] } : prev)}
                  className={chipCls(selected)}>
                  {t(`common:equipmentTypes.${eq}`)}
                </button>
              );
            })}
          </div>
        </section>

        {/* Life Info */}
        <section className="px-6 sm:px-8 py-7 border-t border-[#EEF1F6]">
          {sectionHeader(t('common:lifeInfo.title'))}
          <div className="space-y-5">
            <div>
              <label className="block text-[13px] font-bold text-[#0F1E3D] mb-2">{t('common:lifeInfo.maritalStatus')}</label>
              <div className="flex flex-wrap gap-2">
                {MARITAL_STATUS.map((status) => (
                  <button key={status} type="button" onClick={() => setFormState({ ...formState, maritalStatus: status })} className={chipCls(formState.maritalStatus === status)}>
                    {t(`common:lifeInfo.${status}`)}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-[13px] font-bold text-[#0F1E3D] mb-2">{t('common:lifeInfo.children')}</label>
              <div className="flex flex-wrap gap-2">
                {CHILDREN_STATUS.map((status) => (
                  <button key={status} type="button" onClick={() => setFormState({ ...formState, hasChildren: status })} className={chipCls(formState.hasChildren === status)}>
                    {t(`common:lifeInfo.${status}`)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Save bar */}
      <div className="flex justify-center pt-7">
        <button onClick={handleSave} disabled={saving}
          className="w-full max-w-[340px] px-6 py-3.5 text-sm font-bold text-white bg-[#0F1E3D] rounded-lg hover:bg-[#2D5BFF] disabled:bg-[#E4E8F0] disabled:text-[#A0AAC2] disabled:cursor-not-allowed transition-colors">
          {saving ? t('myProfile.profileSubPage.saving') : t('myProfile.profileSubPage.save')}
        </button>
      </div>
    </div>
  );
};

export default ProfileSubPage;
